/**
 * Client-side helpers to match stay documents against search filters.
 * Uses features, house rules, address, and titles — no extra API fields required.
 */

function norm(s) {
  return (s || '').toLowerCase();
}

/** Concatenate all searchable text for a stay */
export function getStaySearchBlob(stay) {
  const parts = [
    stay.title,
    stay.address,
    stay.city,
    stay.state,
    ...(stay.features || []).map((f) => f.title),
    ...(stay.houseRules || []).map((r) => `${r.ruleName} ${r.ruleValue}`),
  ];
  return norm(parts.filter(Boolean).join(' \n '));
}

function featureTitles(stay) {
  return (stay.features || []).map((f) => norm(f.title));
}

/** Haversine distance in km */
export function haversineKm(lat1, lon1, lat2, lon2) {
  if (
    lat1 == null ||
    lon1 == null ||
    lat2 == null ||
    lon2 == null ||
    Number.isNaN(lat1) ||
    Number.isNaN(lon1)
  ) {
    return Infinity;
  }
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

const WATER_KEYWORDS = {
  Lake: ['lake'],
  Beach: ['beach', 'sea', 'coast', 'seaside'],
  Riverside: ['river', 'riverside', 'waterfront'],
  'Pool-only': null, // handled specially
};

export function matchesWaterbody(stay, selected) {
  if (!selected || selected.length === 0) return true;
  const titles = featureTitles(stay);
  const blob = getStaySearchBlob(stay);

  return selected.some((opt) => {
    if (opt === 'Pool-only') {
      const hasPool = titles.some((t) => t.includes('pool'));
      const hasNaturalWater = ['lake', 'beach', 'river', 'sea', 'waterfront'].some((k) =>
        blob.includes(k)
      );
      return hasPool && !hasNaturalWater;
    }
    const keys = WATER_KEYWORDS[opt] || [opt.toLowerCase()];
    return keys.some((k) => blob.includes(k) || titles.some((t) => t.includes(k)));
  });
}

/** Heuristic: paid vs free pet charges from listing text */
export function inferPetChargesTier(stay) {
  const blob = getStaySearchBlob(stay);
  if (
    /no\s*pet\s*fee|pet(s)?\s*free|free\s*pet|pets?\s*:\s*free|complimentary\s*pet/i.test(blob)
  ) {
    return 'Free';
  }
  if (/pet\s*(fee|charge|₹|rs\.?|rupees?)|charges?\s*for\s*pets?|pet\s*deposit/i.test(blob)) {
    return 'Paid';
  }
  return null;
}

export function matchesPetCharges(stay, filter) {
  if (!filter) return true;
  const tier = inferPetChargesTier(stay);
  if (filter === 'Free') {
    if (tier === 'Free') return true;
    if (tier === 'Paid') return false;
    return true;
  }
  if (filter === 'Paid') {
    if (tier === 'Paid') return true;
    if (tier === 'Free') return false;
    return true;
  }
  return true;
}

/** Max pet charge in ₹ thousands implied in text (very rough) */
export function inferPetChargeMaxK(stay) {
  const blob = getStaySearchBlob(stay);
  const m = blob.match(/pet[^0-9]{0,40}(₹|rs\.?\s*)?\s*([0-9]{3,5})/i);
  if (m) return parseInt(m[2], 10) / 1000;
  return null;
}

export function matchesPetChargesAmount(stay, maxK) {
  if (maxK <= 0) return true;
  const inferred = inferPetChargeMaxK(stay);
  if (inferred == null) return true;
  return inferred <= maxK + 0.01;
}

export function hasPoolMention(stay) {
  return featureTitles(stay).some((t) => t.includes('pool')) || getStaySearchBlob(stay).includes('pool');
}

export function matchesDogsInPool(stay, option) {
  if (!option) return true;
  const blob = getStaySearchBlob(stay);
  const pool = hasPoolMention(stay);
  const separate =
    /separate\s*pet\s*pool|dedicated\s*pet\s*pool|pet\s*pool/i.test(blob) ||
    featureTitles(stay).some((t) => /pet.*pool|pool.*pet/i.test(t));

  if (option === 'Separate Pet Pool') {
    return separate;
  }
  if (option === 'Yes') {
    return pool && (separate || /dogs?\s*(in\s*)?pool|pool.*dog|pet.*swim/i.test(blob));
  }
  if (option === 'No') {
    if (separate) return false;
    if (pool && /dogs?\s*(in\s*)?pool|pool.*dog|pet.*swim/i.test(blob)) return false;
    return true;
  }
  return true;
}

const TREK_KEYS = {
  Trek: ['trek', 'trekking', 'hiking', 'hill'],
  Trail: ['trail', 'walking trail', 'nature trail'],
  Lawn: ['lawn', 'garden', 'grass'],
  'Open Area': ['open area', 'open space', 'grounds', 'acre', 'meadow'],
};

export function matchesTrekTrail(stay, selected) {
  if (!selected || selected.length === 0) return true;
  const blob = getStaySearchBlob(stay);
  return selected.some((opt) => {
    const keys = TREK_KEYS[opt] || [opt.toLowerCase()];
    return keys.some((k) => blob.includes(k));
  });
}

export function matchesMinRating(stay, min) {
  if (!min || min <= 0) return true;
  return (stay.rating || 0) >= min;
}

export function matchesFilterCity(stay, filterCity) {
  if (!filterCity || !String(filterCity).trim()) return true;
  const q = norm(filterCity);
  return norm(stay.city).includes(q) || norm(stay.state).includes(q);
}

export function matchesNearMe(stay, nearMe) {
  if (!nearMe || nearMe.lat == null || nearMe.lng == null) return true;
  const lat = stay.latitude;
  const lon = stay.longitude;
  if (lat == null || lon == null || lat === '' || lon === '') return false;
  const latN = typeof lat === 'string' ? parseFloat(lat) : lat;
  const lonN = typeof lon === 'string' ? parseFloat(lon) : lon;
  const d = haversineKm(nearMe.lat, nearMe.lng, latN, lonN);
  const r = nearMe.radiusKm ?? 50;
  return d <= r;
}
